import { Accordion, AccordionItem, Card, CardBody, Input } from '@nextui-org/react';
import {
    AddConfigurationFieldForm,
    CtyButton,
    GenericConfigurationFieldRenderer,
    formatKeyForDisplay,
    type AddFieldDraft,
} from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { type Dispatch, type SetStateAction } from 'react';
import {
    getByPath,
    isObjectValue,
    isPrimitiveValue,
    stringifyArrayEntry,
    type JsonPrimitive,
    type JsonValue,
} from './agentConfigurationShared';
import {
    accordionItemClassNames,
    getAddFieldPathKey,
    getUpdatedArrayEntryValue,
    passthroughLabel,
} from './difyConfigurationUtils';

export type DifyNestedEditorModel = {
    isConfigurationEditable: boolean;
    requiresCreateConfiguration: boolean;
    expandAll: boolean;
    accordionVersion: number;
    initialConfigState: Record<string, JsonValue>;
    previewModeByPath: Record<string, boolean>;
    addFieldDraftByPath: Record<string, AddFieldDraft>;
    isAddingField: boolean;
    updateField: (path: string[], value: JsonValue) => void;
    handleFieldDelete: (path: string[]) => void;
    setPreviewModeByPath: Dispatch<SetStateAction<Record<string, boolean>>>;
    openAddFieldDraft: (path: string[]) => void;
    clearAddFieldDraft: (path: string[]) => void;
    updateAddFieldDraft: (path: string[], updater: (draft: AddFieldDraft) => AddFieldDraft) => void;
    addFieldAtPath: (path: string[]) => void;
};

type DifySelectedNodeEditorProps = {
    currentSection: string;
    selectedNode: JsonValue | undefined;
    selectedNodePath: string[];
    model: DifyNestedEditorModel;
};

const renderPrimitiveEditor = (
    fieldKey: string,
    value: JsonPrimitive,
    path: string[],
    model: DifyNestedEditorModel
) => {
    const pathKey = path.join('.');
    const initialValue = getByPath(model.initialConfigState, path) as JsonValue;

    return (
        <GenericConfigurationFieldRenderer
            fieldKey={fieldKey}
            fieldLabel={formatKeyForDisplay(fieldKey)}
            value={value}
            initialValue={initialValue}
            isPreview={Boolean(model.previewModeByPath[pathKey])}
            onValueChange={(nextValue) => model.updateField(path, nextValue as JsonValue)}
            onPreviewChange={(nextPreview) =>
                model.setPreviewModeByPath((prev) => ({ ...prev, [pathKey]: nextPreview }))
            }
            onResetToDefault={() => model.updateField(path, initialValue)}
            onDelete={() => model.handleFieldDelete(path)}
            isDisabled={!model.isConfigurationEditable}
            t={passthroughLabel}
        />
    );
};

const renderArrayEditor = (
    fieldKey: string,
    value: JsonValue[],
    path: string[],
    model: DifyNestedEditorModel
) => (
    <Card className="border border-divider shadow-none">
        <CardBody className="gap-2 p-3">
            <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-default-800">{formatKeyForDisplay(fieldKey)}</p>
                <CtyButton
                    size="sm"
                    variant="light"
                    onPress={() => model.updateField(path, [...value, ''])}
                    isDisabled={!model.isConfigurationEditable}
                >
                    Add
                </CtyButton>
            </div>
            {value.map((entry, index) => (
                <div key={`${fieldKey}-${index}`} className="flex items-center gap-2">
                    <Input
                        value={stringifyArrayEntry(entry)}
                        onValueChange={(next) => {
                            const nextArray = [...value];
                            nextArray[index] = getUpdatedArrayEntryValue(entry, next);
                            model.updateField(path, nextArray);
                        }}
                        variant="bordered"
                        size="sm"
                        isDisabled={!model.isConfigurationEditable}
                    />
                    <CtyButton
                        isIconOnly
                        size="sm"
                        variant="light"
                        color="danger"
                        isDisabled={!model.isConfigurationEditable}
                        onPress={() => model.updateField(path, value.filter((_, itemIndex) => itemIndex !== index))}
                    >
                        <Icon icon="mdi:close" width={16} />
                    </CtyButton>
                </div>
            ))}
        </CardBody>
    </Card>
);

const DifyObjectEditor = ({
    value,
    path,
    model,
    showAddField = true,
}: {
    value: Record<string, JsonValue>;
    path: string[];
    model: DifyNestedEditorModel;
    showAddField?: boolean;
}) => {
    const addFieldPathKey = getAddFieldPathKey(path);
    const addFieldDraft = model.addFieldDraftByPath[addFieldPathKey];
    const entries = Object.entries(value);
    const primitiveEntries = entries.filter(([, val]) => isPrimitiveValue(val));
    const nestedEntries = entries.filter(([, val]) => !isPrimitiveValue(val));

    return (
        <div className="flex flex-col gap-3">
            {showAddField && (
                <div className="flex flex-col gap-2">
                    {!addFieldDraft ? (
                        <div className="flex justify-end">
                            <CtyButton
                                size="sm"
                                color="primary"
                                variant="solid"
                                onPress={() => model.openAddFieldDraft(path)}
                                isDisabled={!model.isConfigurationEditable}
                            >
                                Add Field
                            </CtyButton>
                        </div>
                    ) : (
                        <AddConfigurationFieldForm
                            draft={addFieldDraft}
                            isDisabled={!model.isConfigurationEditable}
                            isAdding={model.isAddingField}
                            showConfigId={model.requiresCreateConfiguration && path.length === 0}
                            onDraftChange={(patch) => model.updateAddFieldDraft(path, (draft) => ({ ...draft, ...patch }))}
                            onCancel={() => model.clearAddFieldDraft(path)}
                            onAdd={() => model.addFieldAtPath(path)}
                            t={passthroughLabel}
                        />
                    )}
                </div>
            )}

            {primitiveEntries.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {primitiveEntries.map(([key, val]) => (
                        <div
                            key={`${path.join('.')}.${key}`}
                            className={typeof val === 'string' && key.toLowerCase().includes('prompt') ? 'md:col-span-2' : ''}
                        >
                            {renderPrimitiveEditor(key, val as JsonPrimitive, [...path, key], model)}
                        </div>
                    ))}
                </div>
            )}

            {nestedEntries.length > 0 && (
                <Accordion
                    key={`${path.join('.')}-${model.accordionVersion}`}
                    variant="splitted"
                    selectionMode="multiple"
                    className="!px-0 !gap-2"
                    defaultExpandedKeys={
                        model.expandAll
                            ? nestedEntries.map(([key]) => [...path, key].join('.'))
                            : []
                    }
                >
                    {nestedEntries.map(([key, val]) => {
                        const nodePath = [...path, key];
                        if (Array.isArray(val)) {
                            return (
                                <AccordionItem
                                    key={nodePath.join('.')}
                                    title={formatKeyForDisplay(key)}
                                    classNames={accordionItemClassNames}
                                >
                                    {renderArrayEditor(key, val, nodePath, model)}
                                </AccordionItem>
                            );
                        }
                        return (
                            <AccordionItem
                                key={nodePath.join('.')}
                                title={formatKeyForDisplay(key)}
                                classNames={accordionItemClassNames}
                            >
                                <DifyObjectEditor
                                    value={val as Record<string, JsonValue>}
                                    path={nodePath}
                                    model={model}
                                />
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
};

export function DifySelectedNodeEditor({
    currentSection,
    selectedNode,
    selectedNodePath,
    model,
}: DifySelectedNodeEditorProps) {
    if (isPrimitiveValue(selectedNode as JsonValue)) {
        return renderPrimitiveEditor(currentSection, selectedNode as JsonPrimitive, selectedNodePath, model);
    }

    if (Array.isArray(selectedNode)) {
        return renderArrayEditor(currentSection, selectedNode, selectedNodePath, model);
    }

    if (isObjectValue(selectedNode as JsonValue)) {
        return (
            <DifyObjectEditor
                value={selectedNode as Record<string, JsonValue>}
                path={selectedNodePath}
                model={model}
            />
        );
    }

    return (
        <div className="text-sm text-default-500">
            Selected node is not editable.
        </div>
    );
}
